import React from 'react';
import DOMPurify from 'dompurify';
import { linkifyText } from './linkify';

const ALLOWED_TAGS = ['p', 'strong', 'em', 'b', 'i', 'u', 'a', 'img', 'ul', 'ol', 'li', 'br'];
const ALLOWED_ATTR = ['href', 'target', 'rel', 'src', 'alt', 'class'];

// Explicit URL-scheme whitelist for href/src attributes. DOMPurify v3 strips
// javascript:/vbscript: by default, but pinning the policy here means our
// posture survives any future DOMPurify regression. Allows:
//   - http, https, mailto, tel, sms — common safe schemes
//   - data:image/* — base64 inline images produced by the TipTap editor
//                    when admins paste/upload images into FAQ content
//   - relative URLs starting with / # or .
// Blocks:
//   - javascript:, vbscript: (script execution)
//   - data:text/html,... (HTML injection via data URI)
//   - file:, ftp:, custom schemes
const ALLOWED_URI_REGEXP = /^(?:(?:https?|mailto|tel|sms):|data:image\/|[/#.])/i;

const SANITIZE_OPTIONS = { ALLOWED_TAGS, ALLOWED_ATTR, ALLOWED_URI_REGEXP };

/**
 * Centralized HTML sanitizer. Use this everywhere we render
 * untrusted HTML into the DOM via dangerouslySetInnerHTML.
 *
 * @param {string|null|undefined} content
 * @returns {string} sanitized HTML, or '' if input is empty/nullish
 */
export const sanitizeHTML = (content) => {
  if (!content) return '';
  return DOMPurify.sanitize(content, SANITIZE_OPTIONS);
};

/**
 * Check if a string contains HTML tags (e.g. from TipTap FAQ editor)
 */
export const isHTML = (str) => {
  if (!str) return false;
  return /<[a-z][\s\S]*>/i.test(str);
};

/**
 * Strip HTML tags to plain text (for previews/summaries)
 */
export const stripHTML = (html) => {
  if (!html) return '';
  // Parse into a <template>: its content is owned by an inert document with no
  // browsing context, so <img onerror> / resource-loading never fire on
  // untrusted input — yet it uses the same fragment parser as element.innerHTML,
  // so the extracted text (incl. whitespace) matches the previous behaviour.
  const template = document.createElement('template');
  template.innerHTML = html;
  return template.content.textContent || '';
};

/**
 * Render content with safe HTML support.
 * If content has HTML tags, sanitizes with DOMPurify and renders as HTML.
 * Otherwise renders plain text with bare URLs turned into new-tab anchors —
 * SMS bodies (e.g. the "🔗 <url>" line on proactive event messages) carry
 * links as raw text, and admins need to open them.
 *
 * @param {string} content - Message content (may contain HTML)
 * @param {string} [tag='span'] - Wrapper element ('span' for inline, 'div' for block)
 */
export const renderHTMLContent = (content, tag = 'span') => {
  if (!content) return null;
  if (isHTML(content)) {
    const sanitized = sanitizeHTML(content);
    const props = { dangerouslySetInnerHTML: { __html: sanitized }, className: 'html-content' };
    return React.createElement(tag, props);
  }
  return linkifyText(content);
};
